import React, { useEffect } from 'react';

/**
 * Subtle, non-rotating ambient effects:
 *  - Twinkling altitude dots (stars) — no spin
 *  - Pulsing runway lights at the bottom edge — no spin
 *  - Smooth scroll parallax for [data-parallax-speed]
 *
 * Removed: radar sweep (rotation), light streaks, dashed flight paths.
 */
const AviationEffects = () => {
  useEffect(() => {
    // Pulsing runway lights along the bottom edge
    const createRunwayLights = () => {
      const container = document.createElement('div');
      container.className = 'runway-lights';
      container.style.cssText = `
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 60px;
        pointer-events: none;
        z-index: 1;
        display: flex;
        justify-content: space-around;
        align-items: flex-end;
      `;

      for (let i = 0; i < 24; i++) {
        const light = document.createElement('div');
        light.style.cssText = `
          width: 6px;
          height: 6px;
          background: #D4AF37;
          border-radius: 50%;
          opacity: ${0.1 + Math.random() * 0.2};
          animation: runway-pulse ${Math.random() * 2 + 3}s ease-in-out infinite;
          animation-delay: ${i * 0.12}s;
          box-shadow: 0 0 16px rgba(212, 175, 55, 0.6);
        `;
        container.appendChild(light);
      }
      document.body.appendChild(container);
    };

    // Faint twinkling stars
    const createAltitudeDots = () => {
      const container = document.createElement('div');
      container.className = 'altitude-dots';
      container.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 1;
      `;

      for (let i = 0; i < 35; i++) {
        const dot = document.createElement('div');
        dot.style.cssText = `
          position: absolute;
          width: ${Math.random() * 2 + 1}px;
          height: ${Math.random() * 2 + 1}px;
          background: rgba(212, 175, 55, ${Math.random() * 0.35 + 0.1});
          border-radius: 50%;
          top: ${Math.random() * 100}%;
          left: ${Math.random() * 100}%;
          animation: altitude-twinkle ${Math.random() * 3 + 3}s ease-in-out infinite;
          animation-delay: ${Math.random() * 2}s;
        `;
        container.appendChild(dot);
      }
      document.body.appendChild(container);
    };

    const style = document.createElement('style');
    style.setAttribute('data-aviation-effects', 'true');
    style.textContent = `
      @keyframes runway-pulse {
        0%, 100% {
          opacity: 0.15;
          box-shadow: 0 0 8px rgba(212, 175, 55, 0.3);
        }
        50% {
          opacity: 0.7;
          box-shadow: 0 0 22px rgba(212, 175, 55, 0.7);
        }
      }
      @keyframes altitude-twinkle {
        0%, 100% { opacity: 0.15; }
        50% { opacity: 0.7; }
      }
      [data-parallax-speed="slow"] { transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1); }
      [data-parallax-speed="medium"] { transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
      [data-parallax-speed="fast"] { transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1); }
    `;
    document.head.appendChild(style);

    createRunwayLights();
    createAltitudeDots();

    // Smooth scroll parallax
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrolled = window.pageYOffset;
          document.querySelectorAll('[data-parallax-speed]').forEach(el => {
            const speed = el.dataset.parallaxSpeed;
            let multiplier = 0.4;
            if (speed === 'slow') multiplier = 0.25;
            if (speed === 'medium') multiplier = 0.4;
            if (speed === 'fast') multiplier = 0.6;
            el.style.transform = `translateY(${scrolled * multiplier}px)`;
          });
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.querySelector('.runway-lights')?.remove();
      document.querySelector('.altitude-dots')?.remove();
      document.querySelector('style[data-aviation-effects]')?.remove();
    };
  }, []);

  return null;
};

export default AviationEffects;
